import React from 'react';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

interface RadarChartProps {
  data: {
    labels: string[];
    datasets: Array<{
      label: string;
      data: number[];
      backgroundColor?: string;
      borderColor?: string;
      borderWidth?: number;
      pointBackgroundColor?: string;
    }>;
  };
}

const RadarChart: React.FC<RadarChartProps> = ({ data }) => {
  // RIASEC colors
  const categoryColors = [
    '#FF6384', // R - Red
    '#36A2EB', // I - Blue
    '#FFCE56', // A - Yellow
    '#4BC0C0', // S - Teal
    '#9966FF', // E - Purple
    '#FF9F40'  // C - Orange
  ];

  // Short labels for RIASEC
  const shortLabels = ['R', 'I', 'A', 'S', 'E', 'C'];

  // Helper function to convert hex to rgba
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Create datasets - one for each segment with different colors
  const datasets = data.datasets[0].data.map((value, index) => {
    // Create array with only this value and next value, rest are null
    const segmentData = data.datasets[0].data.map((v, i) => {
      if (i === index || i === (index + 1) % data.datasets[0].data.length) {
        return data.datasets[0].data[i];
      }
      return 0;
    });
    return {
      label: shortLabels[index],
      data: segmentData,
      backgroundColor: hexToRgba(categoryColors[index], 0.5),
      borderColor: categoryColors[index],
      borderWidth: 2,
      // pointBackgroundColor: categoryColors[index],
      // pointBorderColor: '#fff',
      // pointBorderWidth: 2,
      // pointRadius: 5,
      // pointHoverRadius: 7,
    };
  });

  // Add main dataset for complete outline
  const mainDataset: any = {
    label: 'RIASEC',
    data: data.datasets[0].data,
    backgroundColor: 'rgba(102, 126, 234, 0.1)',
    borderColor: 'rgba(102, 126, 234, 0.8)',
    borderWidth: 2,
    pointBackgroundColor: categoryColors,
    pointBorderColor: '#fff',
    pointBorderWidth: 2,
    pointRadius: 6,
    pointHoverRadius: 8,
  };
  
  datasets.push(mainDataset);

  const chartData = {
    labels: data.labels.map((label, index) => `${shortLabels[index]} (${data.datasets[0].data[index]})`),
    datasets: datasets,
  };

  const options: ChartOptions<'radar'> = {
    responsive: true,
    maintainAspectRatio: true,
    scales: {
      r: {
        beginAtZero: true,
        min: 0,
        ticks: {
          stepSize: 2,
          font: {
            size: 10,
          },
          backdropColor: 'transparent',
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        pointLabels: {
          font: {
            size: 12,
            weight: 'bold',
          },
          color: (context) => {
            return categoryColors[context.index];
          },
        },
        angleLines: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          size: 13,
        },
        callbacks: {
          title: function(context) {
            const index = context[0].dataIndex;
            return data.labels[index];
          },
          label: function(context) {
            const value = context.parsed.r;
            if (value > 0) {
              return `Skor: ${value}`;
            }
            return '';
          },
        },
      },
    },
  };

  return (
    <div className="radar-chart-container" style={{ width: '100%', height: '100%', position: 'relative', display: 'flex', justifyContent: 'center' }}>
      <Radar data={chartData} options={options} />
    </div>
  );
};

export default RadarChart;
